import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';

interface PricingPageProps {
  onSignUp?: () => void;
}

export const PricingPage: React.FC<PricingPageProps> = ({ onSignUp }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { checkout, isLoading, error } = useSubscription();
  const [isProcessing, setIsProcessing] = React.useState(false);

  const handleSubscribe = async (planId: string) => {
    if (!user) {
      if (onSignUp) {
        onSignUp();
      } else {
        window.location.href = '/?auth=signup';
      }
      return;
    }

    try {
      setIsProcessing(true);
      const checkoutUrl = await checkout(planId);
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Checkout error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSignUp = () => {
    if (onSignUp) {
      onSignUp();
    } else {
      window.location.href = '/?auth=signup';
    }
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(price / 100);
  };

  const pricingPlans = [
    {
      id: 'free',
      name: t('free.name', { ns: 'pricing' }),
      price: 0,
      currency: 'usd',
      description: t('free.description', { ns: 'pricing' }),
      features: [
        t('free.features.fileConversion', { ns: 'pricing' }),
        t('free.features.currencyConversion', { ns: 'pricing' }),
        t('free.features.basicCategorization', { ns: 'pricing' }),
        t('free.features.download', { ns: 'pricing' }),
        t('free.features.emailSupport', { ns: 'pricing' })
      ],
      limitations: [
        t('free.limitations.noServerConnections', { ns: 'pricing' }),
        t('free.limitations.noImport', { ns: 'pricing' }),
        t('free.limitations.noServerCategorization', { ns: 'pricing' })
      ],
      cta: t('free.cta', { ns: 'pricing' }),
      ctaAction: handleSignUp,
      popular: false
    },
    {
      id: 'basic',
      name: t('paid.name', { ns: 'pricing' }),
      price: 500,
      currency: 'usd',
      description: t('paid.description', { ns: 'pricing' }),
      features: [
        t('paid.features.everythingInFree', { ns: 'pricing' }),
        t('paid.features.serverConnections', { ns: 'pricing' }),
        t('paid.features.transactionImport', { ns: 'pricing' }),
        t('paid.features.serverCategorization', { ns: 'pricing' }),
        t('paid.features.prioritySupport', { ns: 'pricing' }),
        t('paid.features.analytics', { ns: 'pricing' }),
        t('paid.features.unlimitedImports', { ns: 'pricing' })
      ],
      limitations: [],
      cta: t('paid.cta', { ns: 'pricing' }),
      ctaAction: () => handleSubscribe('basic'),
      popular: true
    }
  ];

  const comparisonFeatures = [
    {
      feature: t('comparison.fileConversion', { ns: 'pricing' }),
      free: t('comparison.allBanksSupported', { ns: 'pricing' }),
      paid: t('comparison.allBanksSupported', { ns: 'pricing' })
    },
    {
      feature: t('comparison.currencyConversion', { ns: 'pricing' }),
      free: t('comparison.realTimeRates', { ns: 'pricing' }),
      paid: t('comparison.realTimeRates', { ns: 'pricing' })
    },
    {
      feature: t('comparison.basicCategorization', { ns: 'pricing' }),
      free: t('comparison.localProcessing', { ns: 'pricing' }),
      paid: t('comparison.localProcessing', { ns: 'pricing' })
    },
    {
      feature: t('comparison.actualBudgetConnections', { ns: 'pricing' }),
      free: t('comparison.notAvailable', { ns: 'pricing' }),
      paid: t('comparison.unlimitedServers', { ns: 'pricing' })
    },
    {
      feature: t('comparison.transactionImport', { ns: 'pricing' }),
      free: t('comparison.notAvailable', { ns: 'pricing' }),
      paid: t('comparison.directImport', { ns: 'pricing' })
    },
    {
      feature: t('comparison.serverCategorization', { ns: 'pricing' }),
      free: t('comparison.notAvailable', { ns: 'pricing' }),
      paid: t('comparison.aiPowered', { ns: 'pricing' })
    },
    {
      feature: t('comparison.customerSupport', { ns: 'pricing' }),
      free: t('comparison.emailSupport', { ns: 'pricing' }),
      paid: t('comparison.prioritySupport', { ns: 'pricing' })
    },
    {
      feature: t('comparison.analytics', { ns: 'pricing' }),
      free: t('comparison.notAvailable', { ns: 'pricing' }),
      paid: t('comparison.advancedInsights', { ns: 'pricing' })
    }
  ];

  return (
    <div className="pricing-page">
      <div className="pricing-header">
        <div className="trial-banner">
          <h2>{t('trialBanner', { ns: 'pricing' })}</h2>
          <p>{t('trialDescription', { ns: 'pricing' })}</p>
        </div>
        <h1>{t('title', { ns: 'pricing' })}</h1>
        <p>{t('subtitle', { ns: 'pricing' })}</p>
      </div>

      <div className="pricing-plans">
        {pricingPlans.map((plan) => (
          <div
            key={plan.id}
            className={`pricing-card ${plan.popular ? 'popular' : ''}`}
          >
            {plan.popular && (
              <div className="popular-badge">{t('mostPopular', { ns: 'pricing' })}</div>
            )}

            <div className="plan-header">
              <h3>{plan.name}</h3>
              <div className="price">
                {plan.price === 0 ? (
                  <span className="price-amount">{t('freePrice', { ns: 'pricing' })}</span>
                ) : (
                  <>
                    <span className="price-amount">{formatPrice(plan.price, plan.currency)}</span>
                    <span className="price-interval">{t('perMonth', { ns: 'pricing' })}</span>
                  </>
                )}
              </div>
              <p className="plan-description">{plan.description}</p>
            </div>

            <div className="plan-features">
              <h4>{t('whatsIncluded', { ns: 'pricing' })}</h4>
              <ul>
                {plan.features.map((feature, index) => (
                  <li key={index}>✅ {feature}</li>
                ))}
              </ul>

              {plan.limitations.length > 0 && (
                <>
                  <h4>{t('limitations', { ns: 'pricing' })}</h4>
                  <ul className="limitations">
                    {plan.limitations.map((limitation, index) => (
                      <li key={index}>❌ {limitation}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            <button
              className={`cta-button ${plan.popular ? 'primary' : 'secondary'}`}
              onClick={plan.ctaAction}
              disabled={isProcessing || isLoading}
            >
              {isProcessing || isLoading ? (
                <span className="loading">
                  <svg className="animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('processing', { ns: 'pricing' })}
                </span>
              ) : (
                plan.cta
              )}
            </button>
          </div>
        ))}
      </div>

      <div className="feature-comparison">
        <h2>{t('featureComparison', { ns: 'pricing' })}</h2>
        <div className="comparison-table">
          <div className="comparison-header">
            <div className="feature-column">{t('feature', { ns: 'pricing' })}</div>
            <div className="plan-column">{t('free.name', { ns: 'pricing' })}</div>
            <div className="plan-column">{t('paid.name', { ns: 'pricing' })}</div>
          </div>
          {comparisonFeatures.map((item, index) => (
            <div key={index} className="comparison-row">
              <div className="feature-column">{item.feature}</div>
              <div className="plan-column">{item.free}</div>
              <div className="plan-column">{item.paid}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="pricing-footer">
        <div className="faq-section">
          <h3>{t('faq.title', { ns: 'pricing' })}</h3>
          <div className="faq-grid">
            <div className="faq-item">
              <h4>{t('faq.cancelAnytime.question', { ns: 'pricing' })}</h4>
              <p>{t('faq.cancelAnytime.answer', { ns: 'pricing' })}</p>
            </div>
            <div className="faq-item">
              <h4>{t('faq.freeTrial.question', { ns: 'pricing' })}</h4>
              <p>{t('faq.freeTrial.answer', { ns: 'pricing' })}</p>
            </div>
            <div className="faq-item">
              <h4>{t('faq.paymentMethods.question', { ns: 'pricing' })}</h4>
              <p>{t('faq.paymentMethods.answer', { ns: 'pricing' })}</p>
            </div>
            <div className="faq-item">
              <h4>{t('faq.refunds.question', { ns: 'pricing' })}</h4>
              <p>{t('faq.refunds.answer', { ns: 'pricing' })}</p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
    </div>
  );
};
